import type { RoundName } from "../domain/round/types";
import { ApiError, type TournamentPreparationAccommodation, type TournamentPreparationPayload, type TournamentPreparationVehicle } from "./api";

const API_BASE = String(import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

function detail(body: unknown) {
  if (!body || typeof body !== "object" || !("detail" in body)) return "";
  const value = (body as { detail?: unknown }).detail;
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "message" in value) return String((value as { message?: unknown }).message || "");
  return "";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const isForm = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: {
      Accept: "application/json",
      ...(!isForm && init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json") ? await response.json().catch(() => null) : await response.text().catch(() => "");
  if (!response.ok) throw new ApiError(detail(body) || `HTTP ${response.status}`, response.status, body);
  return body as T;
}

export type EmployeeRecord = {
  id: string;
  name: string;
  aliases: string[];
  active: boolean;
  canDriveGenerally: boolean;
  notes: string;
};

export type EmployeeImportResult = {
  created: EmployeeRecord[];
  updated?: EmployeeRecord[];
  skipped: string[];
  createdCount: number;
  updatedCount?: number;
  skippedCount: number;
};

export type SetupStaff = TournamentPreparationPayload["staff"][number] & {
  roomName: string;
  roundConstraints: Record<string, unknown>;
  transportIncluded: boolean;
};

export type TournamentSetupPayload = Omit<TournamentPreparationPayload, "tournament" | "staff" | "summary"> & {
  tournament: TournamentPreparationPayload["tournament"] & {
    fieldBuffer: number;
    teeTimesUrl: string;
    notes: string;
  };
  staff: SetupStaff[];
  summary: TournamentPreparationPayload["summary"] & {
    employeePoolCount: number;
    activeEmployeePoolCount: number;
  };
};

export type TournamentCreateInput = {
  name: string;
  season: number;
  startDate: string;
  endDate: string;
  location: string;
  country: string;
  copyFromTournamentId?: string;
  copyStaff?: boolean;
};

export type TournamentDetailsInput = {
  expectedRevision: number;
  name: string;
  season: number;
  startDate: string;
  endDate: string;
  status: string;
  location: string;
  country: string;
  fieldBuffer: number;
  teeTimesUrl: string;
  notes: string;
};

export type StaffProfileInput = {
  expectedRevision: number;
  baseId: string;
  roomName: string;
  driverEligible: boolean;
  tvEligible: boolean;
  roles: Record<RoundName, string>;
  notes: string;
  roundConstraints: Record<string, unknown>;
};

export const preparationApi = {
  employees: () => request<{ employees: EmployeeRecord[]; count: number }>("/employees"),

  saveEmployee: (employee: EmployeeRecord) => request<{ employee: EmployeeRecord }>("/employees", {
    method: "POST",
    body: JSON.stringify(employee),
  }),

  deleteEmployee: (employeeId: string) => request<{ ok: boolean; employeeId: string }>(`/employees/${encodeURIComponent(employeeId)}`, {
    method: "DELETE",
  }),

  importEmployeesText: (text: string) => request<EmployeeImportResult>("/employees/import-text", {
    method: "POST",
    body: JSON.stringify({ text, skipExisting: true }),
  }),

  importEmployeesStructured: (employees: EmployeeRecord[], updateExisting = false) => request<EmployeeImportResult>("/employees/import-structured", {
    method: "POST",
    body: JSON.stringify({ employees, updateExisting }),
  }),

  importEmployeePreset2026: () => request<EmployeeImportResult>("/employees/preset-2026", {
    method: "POST",
  }),

  tournamentSetup: (tournamentId: string) => request<TournamentSetupPayload>(`/tournaments/${encodeURIComponent(tournamentId)}/setup`),

  createTournament: (payload: TournamentCreateInput) => request<TournamentSetupPayload>("/tournaments", {
    method: "POST",
    body: JSON.stringify(payload),
  }),

  saveTournamentDetails: (tournamentId: string, payload: TournamentDetailsInput) => request<TournamentSetupPayload>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/details`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),

  saveLogistics: (tournamentId: string, payload: {
    expectedRevision: number;
    accommodations: TournamentPreparationAccommodation[];
    vehicles: Array<Omit<TournamentPreparationVehicle, "baseName">>;
    vehicleKeyPolicy: string;
  }) => request<TournamentSetupPayload>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/logistics`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),

  saveStaffProfile: (tournamentId: string, personId: string, payload: StaffProfileInput) => request<TournamentSetupPayload>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/staff/${encodeURIComponent(personId)}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }),

  addStaff: (tournamentId: string, payload: { expectedRevision: number; employeeIds: string[]; role: string; baseId: string }) => request<{ added: number; missing: string[]; setup: TournamentSetupPayload }>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/staff/add`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),

  removeStaff: (tournamentId: string, payload: { expectedRevision: number; personIds: string[] }) => request<{ removed: number; setup: TournamentSetupPayload }>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/staff/remove`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),

  importStaffText: (tournamentId: string, payload: { expectedRevision: number; text: string; role: string; createMissing: boolean }) => request<{ added: number; alreadyPresent: number; createdEmployees: number; missing: string[]; setup: TournamentSetupPayload }>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/staff/import-text`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),

  importStaffXlsx: (tournamentId: string, file: File, payload: { expectedRevision: number; role: string; createMissing: boolean }) => {
    const form = new FormData();
    form.set("file", file);
    form.set("expectedRevision", String(payload.expectedRevision));
    form.set("role", payload.role);
    form.set("createMissing", payload.createMissing ? "true" : "false");
    return request<{ added: number; alreadyPresent: number; createdEmployees: number; missing: string[]; setup: TournamentSetupPayload }>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/staff/import-xlsx`, {
      method: "POST",
      body: form,
    });
  },

  importRosterLogistics: (tournamentId: string, payload: {
    expectedRevision: number;
    text: string;
    createMissing: boolean;
    replaceRoster: boolean;
    replaceLogistics: boolean;
    defaultTravelMinutes: number;
  }) => request<{
    counts: Record<string, number>;
    warnings: string[];
    createdEmployees: number;
    added: number;
    updated: number;
    missing: string[];
    setup: TournamentSetupPayload;
  }>(`/tournaments/${encodeURIComponent(tournamentId)}/setup/import-logistics`, {
    method: "POST",
    body: JSON.stringify(payload),
  }),
};
